import * as React from 'react';
import { st, classes } from './WeeklyDatePicker.st.css';
import { useTPAComponentsContext } from '../TPAComponentsConfig';
import { getWeekDatesRange, WeeklyDateNavigation, } from '../WeeklyDateNavigation';
import { isFirstDayOfMonth, addMonths, isLastDayOfMonth, addWeeks, getWeek, isSameDay, } from 'date-fns';
import { DayPicker, Row, useDayRender, Button, } from 'react-day-picker';
import { formatDay, formatLongWeekDayName, formatShortWeekDayName, getFirstAvailableDate, } from './util/dateUtils';
import { areDatesInTheSameWeek } from '../WeeklyDateNavigation/dateUtils/dateUtils';
import { useWeeklyDatePickerContext, WeeklyDatePickerProvider, } from './WeeklyDatePickerProvider';
/** A weekly Date Picker to select a specific date. */
const CaptionElement = ({ rtl, mobile, leftArrowAriaLabel, leftArrowAriaLabelledBy, rightArrowAriaLabel, rightArrowAriaLabelledBy, locale, firstDayOfWeek, removeOuterPadding, }) => {
    const Caption = (props) => {
        const { currentWeek, setCurrentMonth, setCurrentWeek } = useWeeklyDatePickerContext();
        const onChange = (week) => {
            setCurrentWeek(week);
            setCurrentMonth(week.startOfWeek);
        };
        return (React.createElement(WeeklyDateNavigation, { captionId: props.id, className: st(classes.header, {
                removeOuterPadding,
                isMobile: mobile,
                rtl,
            }), value: currentWeek, onChange: onChange, prevArrowAriaLabel: leftArrowAriaLabel, prevArrowAriaLabelledBy: leftArrowAriaLabelledBy, nextArrowAriaLabel: rightArrowAriaLabel, nextArrowAriaLabelledBy: rightArrowAriaLabelledBy, locale: locale, firstDayOfWeek: firstDayOfWeek }));
    };
    return Caption;
};
const WeekView = ({ firstDayOfWeek, }) => {
    const Week = (propsRow) => {
        const { currentWeek } = useWeeklyDatePickerContext();
        const weekNumber = getWeek(currentWeek.startOfWeek, {
            weekStartsOn: firstDayOfWeek,
        });
        if (!(weekNumber === propsRow.weekNumber)) {
            return React.createElement(React.Fragment, null);
        }
        return React.createElement(Row, { ...propsRow });
    };
    return Week;
};
const CustomDay = ({ dateIndication, locale, firstAvailableDate, }) => {
    const Day = (propsDay) => {
        const buttonRef = React.useRef(null);
        const dayRender = useDayRender(propsDay.date, propsDay.displayMonth, buttonRef);
        const day = propsDay.date;
        const isSelected = isSameDay(day, dayRender.selectedDays);
        const dateIndicationNode = dateIndication &&
            dateIndication({
                date: day,
                isSelected,
            });
        const buttonProps = { ...dayRender.buttonProps, tabIndex: 0 };
        const isFirstAvailableDate = isSameDay(firstAvailableDate, day);
        const calculateTabIndex = () => {
            if (isFirstAvailableDate) {
                return 0;
            }
            return -1;
        };
        return (React.createElement(Button, { ...buttonProps, ref: buttonRef, "data-date": `${day.getFullYear()}-${day.getMonth()}-${day.getDate()}`, tabIndex: calculateTabIndex() },
            React.createElement("div", { className: st(classes.dayWrapper, {
                    hasIndication: !!dateIndicationNode,
                }) },
                React.createElement("span", { "aria-hidden": "true" }, day.getDate()),
                React.createElement("span", { className: st(classes.vhidden) }, formatDay(day, locale)),
                dateIndicationNode ? (React.createElement("div", { className: classes.dayIndicationContainer }, dateIndicationNode)) : null)));
    };
    return Day;
};
const WeeklyDatePickerContent = ({ locale = 'en', excludePastDates = false, filterDate = () => true, showMonthDropdown = false, showYearDropdown = false, dateIndication = () => null, className, value, onChange, today, firstDayOfWeek = 1, 'data-hook': dataHook, 'aria-label': ariaLabel, 'aria-labelledby': ariaLabelledBy, leftArrowAriaLabel, leftArrowAriaLabelledBy, rightArrowAriaLabel, rightArrowAriaLabelledBy, removeOuterPadding, }) => {
    const { rtl, mobile } = useTPAComponentsContext();
    const { currentMonth, currentWeek, setCurrentMonth, setCurrentWeek } = useWeeklyDatePickerContext();
    const [prevValue, setPrevValue] = React.useState(value);
    const WeeklyView = React.useCallback(WeekView({ firstDayOfWeek }), [
        firstDayOfWeek,
    ]);
    const firstAvailableDate = React.useMemo(() => getFirstAvailableDate({
        startOfWeekDate: currentWeek.startOfWeek,
        filterDate,
        excludePastDates,
    }), [currentWeek.startOfWeek.getDate(), excludePastDates]);
    const Day = React.useCallback(CustomDay({
        dateIndication,
        locale,
        firstAvailableDate,
    }), [dateIndication, firstDayOfWeek]);
    const Caption = React.useCallback(CaptionElement({
        rtl,
        mobile,
        leftArrowAriaLabelledBy,
        leftArrowAriaLabel,
        rightArrowAriaLabel,
        rightArrowAriaLabelledBy,
        locale,
        firstDayOfWeek,
        removeOuterPadding,
    }), [
        rtl,
        mobile,
        leftArrowAriaLabelledBy,
        leftArrowAriaLabel,
        rightArrowAriaLabel,
        rightArrowAriaLabelledBy,
        locale,
        firstDayOfWeek,
        removeOuterPadding,
    ]);
    const updateWeekRange = () => {
        if (!isSameDay(value, prevValue)) {
            setPrevValue(value);
            const latestWeek = getWeekDatesRange(value, firstDayOfWeek);
            if (!areDatesInTheSameWeek(currentWeek.startOfWeek, latestWeek.startOfWeek, firstDayOfWeek)) {
                setCurrentWeek(latestWeek, () => setCurrentMonth(latestWeek.startOfWeek));
            }
        }
    };
    const focusNextDay = (date) => {
        if (isSameDay(date, currentWeek.endOfWeek)) {
            const nextWeek = getWeekDatesRange(addWeeks(currentWeek.startOfWeek, 1), firstDayOfWeek);
            setCurrentWeek(nextWeek);
        }
        if (isLastDayOfMonth(date)) {
            setCurrentMonth(addMonths(date, 1));
        }
    };
    const focusPreviousDay = (date) => {
        if (isSameDay(date, currentWeek.startOfWeek)) {
            const prevWeek = getWeekDatesRange(addWeeks(currentWeek.startOfWeek, -1), firstDayOfWeek);
            setCurrentWeek(prevWeek);
        }
        if (isFirstDayOfMonth(date)) {
            setCurrentMonth(addMonths(date, -1));
        }
    };
    const onDayKeyDownFactory = React.useCallback((_value, _modifiers, event = null) => {
        switch (event.key) {
            case 'ArrowLeft':
                event.preventDefault();
                event.stopPropagation();
                rtl ? focusNextDay(_value) : focusPreviousDay(_value);
                break;
            case 'ArrowRight':
                event.preventDefault();
                event.stopPropagation();
                rtl ? focusPreviousDay(_value) : focusNextDay(_value);
                break;
            default:
        }
    }, [currentWeek]);
    // this is needed because react-day-picker is expected to locale as date-fns but we are using Intl.
    // the formatter is cutomize in order to support Wix locale base on Intl and not date-fns.
    const formatWeekdayName = (date) => {
        return formatShortWeekDayName(date, locale);
    };
    const labelWeekday = (date) => {
        return formatLongWeekDayName(date, locale);
    };
    const classNames = {
        ...classes,
        table: st(classes.table, { isMobile: mobile }),
        day: st(classes.day, 'DayPicker-Day'),
        head: classes.weekdays,
        head_cell: classes.weekday,
    };
    updateWeekRange();
    return (React.createElement("div", { className: st(classes.root, { rtl }, className), "data-hook": dataHook, "aria-label": ariaLabel, "aria-labelledby": ariaLabelledBy, role: "region" },
        React.createElement(DayPicker, { components: {
                Caption,
                Day,
                Row: WeeklyView,
            }, showOutsideDays: true, mode: "single", selected: value, onSelect: onChange, classNames: classNames, captionLayout: showMonthDropdown && showYearDropdown ? 'dropdown' : 'buttons', weekStartsOn: firstDayOfWeek, month: currentMonth, onDayKeyDown: onDayKeyDownFactory, disabled: [
                (date) => !filterDate(new Date(date)),
                ...(excludePastDates ? [{ before: new Date() }] : []),
            ], today: today, formatters: { formatWeekdayName }, labels: { labelWeekday }, required: true })));
};
export const WeeklyDatePicker = ({ value = new Date(), ...restProps }) => (React.createElement(WeeklyDatePickerProvider, { firstDayOfWeek: restProps.firstDayOfWeek, onDisplayedWeekChange: restProps.onDisplayedWeekChange, selectedDate: value },
    React.createElement(WeeklyDatePickerContent, { ...restProps, value: value })));
WeeklyDatePicker.displayName = 'WeeklyDatePicker';
//# sourceMappingURL=WeeklyDatePicker.js.map